/**
 * Bookings filter bar — three tiers.
 *
 *   Tier 1 (inline)  : search, pickup date, status, payment, vehicle
 *   Tier 2 (popover) : everything else, behind "More filters (n)"
 *   Tier 3 (chips)   : what is currently active, each individually clearable
 *
 * ~18 filters cannot all sit inline without burying the table, but hiding them
 * without the chips would let an operator misread a filtered total as the real
 * one. The three tiers are what balance those.
 */

import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { AlertTriangle, Search, SlidersHorizontal } from 'lucide-react'
import {
  BOOKING_SERVICE_TYPE_OPTIONS,
  BOOKING_STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  countActiveFilters,
  type BookingFilters,
} from '@limo/shared'
import { useAccountVehicles, useUtmOptions } from '@/hooks/useReports'
import { MultiSelectFilter } from './MultiSelectFilter'
import { NumberRangeFilter } from './NumberRangeFilter'
import { ReportDateRangeFilter } from './ReportDateRangeFilter'
import { ActiveFilterChips } from './ActiveFilterChips'
import { buildFilterChips } from './filterChips'

interface Props {
  readonly filters: BookingFilters
  readonly timezone: string
  readonly onChange: (next: BookingFilters) => void
  readonly onClearAll: () => void
}

/** Tri-state boolean control: unset / yes / no. */
function TriStateCheckbox({
  label,
  value,
  onChange,
}: {
  readonly label: string
  readonly value: boolean | undefined
  readonly onChange: (next: boolean | undefined) => void
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <Checkbox
        checked={value === true}
        onCheckedChange={(checked) => onChange(checked === true ? true : undefined)}
        aria-label={label}
      />
      <span>{label}</span>
    </label>
  )
}

export function BookingsFilterBar({ filters, timezone, onChange, onClearAll }: Props) {
  const [moreOpen, setMoreOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const vehicles = useAccountVehicles()
  const utm = useUtmOptions()

  const vehicleOptions = (vehicles.data ?? []).map((v) => ({ value: v.id, label: v.name }))
  const toOptions = (values: readonly string[]) => values.map((v) => ({ value: v, label: v }))

  /** Immutable partial update — never mutate the incoming filters object. */
  const patch = (next: Partial<BookingFilters>): void => {
    onChange({ ...filters, ...next })
  }

  const activeCount = countActiveFilters(filters)

  const chips = buildFilterChips(filters as BookingFilters & never, patch as never, {
    bookingStatusOptions: BOOKING_STATUS_OPTIONS,
    paymentStatusOptions: PAYMENT_STATUS_OPTIONS,
    serviceTypeOptions: BOOKING_SERVICE_TYPE_OPTIONS,
    quoteStatusOptions: [],
    vehicleOptions,
  })

  /** Tier 2 body — reused verbatim by the desktop popover and the mobile sheet. */
  const moreFilters = (
    <div className="space-y-5">
      <section className="space-y-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Dates
        </h4>
        <div className="flex flex-col gap-2">
          <ReportDateRangeFilter
            label="Pickup date"
            value={filters.pickupDate}
            timezone={timezone}
            onChange={(pickupDate) => patch({ pickupDate })}
          />
          <ReportDateRangeFilter
            label="Created date"
            value={filters.createdDate}
            timezone={timezone}
            onChange={(createdDate) => patch({ createdDate })}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Money
        </h4>
        <NumberRangeFilter
          label="Total amount"
          currency
          min={filters.amountMin}
          max={filters.amountMax}
          onChange={({ min, max }) => patch({ amountMin: min, amountMax: max })}
        />
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox
            checked={filters.hasBalanceDue === true}
            onCheckedChange={(checked) =>
              patch({ hasBalanceDue: checked === true ? true : undefined })
            }
            aria-label="Has balance due"
          />
          <span>Has balance due (unpaid or partial)</span>
        </label>
      </section>

      <section className="space-y-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Trip
        </h4>
        <div className="flex flex-wrap gap-2">
          <MultiSelectFilter
            label="Service type"
            options={BOOKING_SERVICE_TYPE_OPTIONS}
            selected={filters.serviceType ?? []}
            onChange={(v) =>
              patch({ serviceType: v.length ? (v as BookingFilters['serviceType']) : undefined })
            }
          />
        </div>
        <NumberRangeFilter
          label="Passengers"
          min={filters.passengerMin}
          max={filters.passengerMax}
          onChange={({ min, max }) => patch({ passengerMin: min, passengerMax: max })}
        />
        <div className="space-y-2">
          <TriStateCheckbox
            label="Round trip only"
            value={filters.roundTrip}
            onChange={(roundTrip) => patch({ roundTrip })}
          />
          <TriStateCheckbox
            label="Airport pickup"
            value={filters.airportPickup}
            onChange={(airportPickup) => patch({ airportPickup })}
          />
          <TriStateCheckbox
            label="Airport dropoff"
            value={filters.airportDropoff}
            onChange={(airportDropoff) => patch({ airportDropoff })}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Attribution
        </h4>
        <div className="flex flex-wrap gap-2">
          <MultiSelectFilter
            label="UTM source"
            options={toOptions(utm.data?.sources ?? [])}
            selected={filters.utmSource ?? []}
            onChange={(v) => patch({ utmSource: v.length ? v : undefined })}
            emptyHint="No campaign data recorded yet"
          />
          <MultiSelectFilter
            label="UTM medium"
            options={toOptions(utm.data?.mediums ?? [])}
            selected={filters.utmMedium ?? []}
            onChange={(v) => patch({ utmMedium: v.length ? v : undefined })}
            emptyHint="No campaign data recorded yet"
          />
          <MultiSelectFilter
            label="UTM campaign"
            options={toOptions(utm.data?.campaigns ?? [])}
            selected={filters.utmCampaign ?? []}
            onChange={(v) => patch({ utmCampaign: v.length ? v : undefined })}
            emptyHint="No campaign data recorded yet"
          />
        </div>
        {/*
          Required, not decorative. Attribution is matched by joining through
          booking_quotes, so bookings taken by phone or created before quotes
          existed can never match. Without this note a shrunken count reads as
          "the campaign underperformed" rather than "these rows have no
          attribution data".
        */}
        <p className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-[11px] text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Only bookings created from a website quote can match an attribution filter — phone-in
          bookings have no campaign data.
        </p>
      </section>

      <section className="space-y-3 border-t pt-4">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Advanced
        </h4>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox
            checked={filters.includeTest === true}
            onCheckedChange={(checked) =>
              patch({ includeTest: checked === true ? true : undefined })
            }
            aria-label="Include test bookings"
          />
          <span>Include test bookings</span>
        </label>
        <p className="text-[11px] text-muted-foreground">
          Test bookings are excluded by default so revenue figures stay accurate.
        </p>
      </section>
    </div>
  )

  return (
    <div className="space-y-3">
      {/* Tier 1 */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, confirmation code, or address…"
            value={filters.search ?? ''}
            onChange={(e) => patch({ search: e.target.value || undefined })}
            className="pl-9"
            aria-label="Search bookings"
          />
        </div>

        <div className="hidden flex-wrap items-center gap-2 sm:flex">
          <ReportDateRangeFilter
            label="Pickup"
            value={filters.pickupDate}
            timezone={timezone}
            onChange={(pickupDate) => patch({ pickupDate })}
          />
          <MultiSelectFilter
            label="Status"
            options={BOOKING_STATUS_OPTIONS}
            selected={filters.bookingStatus ?? []}
            onChange={(v) =>
              patch({ bookingStatus: v.length ? (v as BookingFilters['bookingStatus']) : undefined })
            }
          />
          <MultiSelectFilter
            label="Payment"
            options={PAYMENT_STATUS_OPTIONS}
            selected={filters.paymentStatus ?? []}
            onChange={(v) =>
              patch({ paymentStatus: v.length ? (v as BookingFilters['paymentStatus']) : undefined })
            }
          />
          <MultiSelectFilter
            label="Vehicle"
            options={vehicleOptions}
            selected={filters.vehicleIds ?? []}
            onChange={(v) => patch({ vehicleIds: v.length ? v : undefined })}
            emptyHint="No vehicles configured"
          />

          <Popover open={moreOpen} onOpenChange={setMoreOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                More filters
                {activeCount > 0 && <span className="text-xs">({activeCount})</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="max-h-[70vh] w-96 overflow-y-auto" align="end">
              {moreFilters}
            </PopoverContent>
          </Popover>
        </div>

        {/* Mobile: a 20-control popover is unusable at 375px, so use a Sheet. */}
        <div className="sm:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="w-full gap-2">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Filters
                {activeCount > 0 && <span className="text-xs">({activeCount})</span>}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-5">
                <div className="flex flex-wrap gap-2">
                  <MultiSelectFilter
                    label="Status"
                    options={BOOKING_STATUS_OPTIONS}
                    selected={filters.bookingStatus ?? []}
                    onChange={(v) =>
                      patch({
                        bookingStatus: v.length
                          ? (v as BookingFilters['bookingStatus'])
                          : undefined,
                      })
                    }
                  />
                  <MultiSelectFilter
                    label="Payment"
                    options={PAYMENT_STATUS_OPTIONS}
                    selected={filters.paymentStatus ?? []}
                    onChange={(v) =>
                      patch({
                        paymentStatus: v.length
                          ? (v as BookingFilters['paymentStatus'])
                          : undefined,
                      })
                    }
                  />
                  <MultiSelectFilter
                    label="Vehicle"
                    options={vehicleOptions}
                    selected={filters.vehicleIds ?? []}
                    onChange={(v) => patch({ vehicleIds: v.length ? v : undefined })}
                    emptyHint="No vehicles configured"
                  />
                </div>
                {moreFilters}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Tier 3 */}
      <ActiveFilterChips chips={chips} onClearAll={onClearAll} />
    </div>
  )
}
